import React, { useState, useEffect, useRef } from 'react';
import { Activity, Server, Database, Cpu, HardDrive, Zap, AlertCircle, CheckCircle, Clock, TrendingUp, Users, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config/API_URL';

// Circular Gauge Component
const CircularGauge = ({ value, max = 100, label, unit = '%', color, icon: Icon }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const getColor = () => {
    if (percentage > 90) return '#ff3366';
    if (percentage > 70) return '#ffaa00';
    return color;
  };

  const displayColor = getColor();

  return (
    <div style={{
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      padding: '1.5rem',
      border: `1px solid ${displayColor}30`,
      boxShadow: `0 4px 30px ${displayColor}20`,
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '150px',
        height: '150px',
        background: `radial-gradient(circle, ${displayColor}15 0%, transparent 70%)`,
        borderRadius: '50%',
      }} />

      <div style={{
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          position: 'relative',
          width: '160px',
          height: '160px',
          margin: '0 auto 1rem',
        }}>
          <svg
            width="160"
            height="160"
            style={{
              transform: 'rotate(-90deg)',
            }}
          >
            <circle
              cx="80"
              cy="80"
              r={radius}
              fill="none"
              stroke="rgba(255, 255, 255, 0.1)"
              strokeWidth="12"
            />
            <circle
              cx="80"
              cy="80"
              r={radius}
              fill="none"
              stroke={displayColor}
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transition: 'stroke-dashoffset 0.5s ease',
                filter: `drop-shadow(0 0 8px ${displayColor})`,
              }}
            />
          </svg>
          
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}>
            {Icon && <Icon size={28} style={{ color: displayColor, marginBottom: '0.25rem' }} />}
            <div style={{
              fontSize: '2rem',
              fontWeight: 900,
              fontFamily: "'Orbitron', monospace",
              color: displayColor,
            }}>
              {Math.round(value)}
              <span style={{ fontSize: '1rem' }}>{unit}</span>
            </div>
          </div>
        </div>

        <div style={{
          fontSize: '1rem',
          color: '#a0a0c0',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          fontFamily: "'Rajdhani', sans-serif",
        }}>
          {label}
        </div>
      </div>
    </div>
  );
};

// Heart Monitor Chart Component
const HeartMonitorChart = ({ data, label, color, unit = 'ms', onViewUsers }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: 200,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (!canvasRef.current || dimensions.width === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { width, height } = dimensions;

    canvas.width = width;
    canvas.height = height;

    ctx.clearRect(0, 0, width, height);

    // Draw grid
    ctx.strokeStyle = 'rgba(0, 255, 245, 0.05)';
    ctx.lineWidth = 1;
    
    const gridSize = 20;
    for (let x = 0; x < width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    for (let y = 0; y < height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    if (data.length < 2) return;

    const maxValue = Math.max(...data, 100);
    const minValue = Math.min(...data, 0);
    const range = maxValue - minValue || 1;
    const padding = 20;

    // Draw line
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.shadowColor = color;
    ctx.shadowBlur = 10;

    data.forEach((value, index) => {
      const x = (index / (data.length - 1)) * (width - padding * 2) + padding;
      const y = height - padding - ((value - minValue) / range) * (height - padding * 2);

      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();
    ctx.shadowBlur = 0;

    // Draw glow effect
    ctx.beginPath();
    data.forEach((value, index) => {
      const x = (index / (data.length - 1)) * (width - padding * 2) + padding;
      const y = height - padding - ((value - minValue) / range) * (height - padding * 2);
      
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });
    ctx.lineTo(width - padding, height - padding);
    ctx.lineTo(padding, height - padding);
    ctx.closePath();
    
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, `${color}30`);
    gradient.addColorStop(1, 'transparent');
    ctx.fillStyle = gradient;
    ctx.fill();

  }, [data, dimensions, color]);

  const currentValue = data[data.length - 1] || 0;
  const previousValue = data[data.length - 2] || currentValue;
  const trend = currentValue - previousValue;

  return (
    <div style={{
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      padding: '1.5rem',
      border: `1px solid ${color}30`,
      boxShadow: `0 4px 30px ${color}20`,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
      }}>
        <div>
          <div style={{
            fontSize: '0.9rem',
            color: '#a0a0c0',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontFamily: "'Rajdhani', sans-serif",
            marginBottom: '0.25rem',
          }}>
            {label}
          </div>
          <div style={{
            fontSize: '2rem',
            fontWeight: 900,
            fontFamily: "'Orbitron', monospace",
            color: color,
          }}>
            {currentValue.toFixed(0)}
            <span style={{ fontSize: '1rem', marginLeft: '0.25rem' }}>{unit}</span>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {trend !== 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem',
              fontWeight: 600,
              color: trend > 0 ? '#ff3366' : '#00ff88',
            }}>
              {trend > 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(0)}{unit}
            </div>
          )}
          
          {onViewUsers && (
            <button
              onClick={onViewUsers}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: `${color}20`,
                border: `1px solid ${color}`,
                borderRadius: '8px',
                padding: '0.5rem 1rem',
                color: color,
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `${color}40`;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `${color}20`;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Users size={16} />
              View Users
            </button>
          )}
        </div>
      </div>

      <div ref={containerRef} style={{ width: '100%', height: '200px', position: 'relative' }}>
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} />
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '0.5rem',
        fontSize: '0.85rem',
        color: '#8b5cf6',
        fontFamily: "'Rajdhani', sans-serif",
      }}>
        <span>Last {data.length} readings</span>
        <span>Updated: {new Date().toLocaleTimeString()}</span>
      </div>
    </div>
  );
};

// Users Modal Component
const UsersModal = ({ isOpen, onClose, users, loading }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem',
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'rgba(20, 20, 35, 0.95)',
            borderRadius: '20px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            boxShadow: '0 20px 60px rgba(139, 92, 246, 0.3)',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}>
              <Users size={28} style={{ color: '#8b5cf6' }} />
              <h2 style={{
                fontSize: '1.8rem',
                fontWeight: 700,
                fontFamily: "'Orbitron', monospace",
                color: '#e0e0ff',
                margin: 0,
              }}>
                Registered Users
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#a0a0c0',
                cursor: 'pointer',
                padding: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#a0a0c0';
              }}
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div style={{
            padding: '1.5rem 2rem',
            overflowY: 'auto',
            flex: 1,
          }}>
            {loading ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#a0a0c0',
                fontFamily: "'Rajdhani', sans-serif",
              }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Users size={48} style={{ color: '#8b5cf6', marginBottom: '1rem' }} />
                </motion.div>
                Loading users...
              </div>
            ) : users.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                color: '#a0a0c0',
                fontFamily: "'Rajdhani', sans-serif",
              }}>
                No users found
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
              }}>
                {users.map((user, index) => (
                  <motion.div
                    key={user.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    style={{
                      background: 'rgba(139, 92, 246, 0.1)',
                      border: '1px solid rgba(139, 92, 246, 0.2)',
                      borderRadius: '12px',
                      padding: '1rem 1.5rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                      e.currentTarget.style.transform = 'translateX(5px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <div>
                      {user.name && (
                        <div style={{
                          fontSize: '1.2rem',
                          fontWeight: 700,
                          color: '#00fff5',
                          fontFamily: "'Orbitron', monospace",
                          marginBottom: '0.25rem',
                        }}>
                          {user.name}
                        </div>
                      )}
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: '#e0e0ff',
                        fontFamily: "'Rajdhani', sans-serif",
                        marginBottom: '0.25rem',
                      }}>
                        {user.email}
                      </div>
                      {user.created_at && (
                        <div style={{
                          fontSize: '0.85rem',
                          color: '#a0a0c0',
                          fontFamily: "'Rajdhani', sans-serif",
                        }}>
                          Registered: {new Date(user.created_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    {user.is_admin && (
                      <span style={{
                        background: 'rgba(255, 170, 0, 0.2)',
                        color: '#ffaa00',
                        padding: '0.25rem 0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        fontFamily: "'Rajdhani', sans-serif",
                        border: '1px solid rgba(255, 170, 0, 0.3)',
                      }}>
                        ADMIN
                      </span>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {!loading && users.length > 0 && (
            <div style={{
              padding: '1rem 2rem',
              borderTop: '1px solid rgba(139, 92, 246, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '0.9rem',
              color: '#a0a0c0',
              fontFamily: "'Rajdhani', sans-serif",
            }}>
              <span>Total Users: {users.length}</span>
              <span>Admin Users: {users.filter(u => u.is_admin).length}</span>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Status Indicator Component
const StatusIndicator = ({ status, lastCheck }) => {
  const isHealthy = status === 'healthy';
  
  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        background: isHealthy 
          ? 'rgba(0, 255, 136, 0.1)' 
          : 'rgba(255, 51, 102, 0.1)',
        border: `1px solid ${isHealthy ? '#00ff88' : '#ff3366'}`,
        borderRadius: '12px',
        padding: '1rem 1.5rem',
        backdropFilter: 'blur(20px)',
      }}
    >
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        {isHealthy ? (
          <CheckCircle size={24} style={{ color: '#00ff88' }} />
        ) : (
          <AlertCircle size={24} style={{ color: '#ff3366' }} />
        )}
      </motion.div>
      
      <div>
        <div style={{
          fontSize: '1rem',
          fontWeight: 700,
          color: isHealthy ? '#00ff88' : '#ff3366',
          fontFamily: "'Orbitron', monospace",
          textTransform: 'uppercase',
          letterSpacing: '1px',
        }}>
          System {isHealthy ? 'Healthy' : 'Issues Detected'}
        </div>
        <div style={{
          fontSize: '0.85rem',
          color: '#a0a0c0',
          fontFamily: "'Rajdhani', sans-serif",
          marginTop: '0.25rem',
        }}>
          Last check: {lastCheck}
        </div>
      </div>
    </motion.div>
  );
};

// Main System Page Component
const SystemPage = () => {
  const [healthData, setHealthData] = useState({
    status: 'unknown',
    lastCheck: 'Never',
    responseTime: 0,
  });
  
  const [metrics, setMetrics] = useState({
    responseTime: [],
    userCount: [],
    cpuUsage: 0,
    memoryUsage: 0,
    diskUsage: 0,
    activeConnections: 0,
  });

  const [loading, setLoading] = useState(true);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const maxDataPoints = 30;

  const performHealthCheck = async () => {
    const startTime = Date.now();
    
    try {
      const response = await fetch(`${API_URL}/admin/health`, {
        credentials: 'include',
      });
      
      const endTime = Date.now();
      const responseTime = endTime - startTime;
      
      const data = await response.json();
      
      setHealthData({
        status: data.status,
        lastCheck: new Date().toLocaleTimeString(),
        responseTime: responseTime,
      });

      setMetrics(prev => ({
        ...prev,
        responseTime: [...prev.responseTime.slice(-maxDataPoints + 1), responseTime],
      }));

      await fetchUserCount();
      
    } catch (error) {
      console.error('Health check failed:', error);
      setHealthData({
        status: 'error',
        lastCheck: new Date().toLocaleTimeString(),
        responseTime: 0,
      });
    }
  };

  const fetchUserCount = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/metrics/users`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        const userCount = data.metrics.total;
        
        setMetrics(prev => ({
          ...prev,
          userCount: [...prev.userCount.slice(-maxDataPoints + 1), userCount],
        }));
      }
    } catch (error) {
      console.error('Failed to fetch user count:', error);
    }
  };

  const fetchUsersList = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API_URL}/admin/metrics/users`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsersList(data.metrics.recent_users || []);
      }
    } catch (error) {
      console.error('Failed to fetch users list:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleViewUsers = () => {
    setShowUsersModal(true);
    fetchUsersList();
  };

  const updateSystemMetrics = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/metrics/system`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        const systemMetrics = data.metrics;
        
        setMetrics(prev => ({
          ...prev,
          cpuUsage: systemMetrics.cpu.percent,
          memoryUsage: systemMetrics.memory.percent,
          diskUsage: systemMetrics.disk.percent,
          activeConnections: systemMetrics.network.connections,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch system metrics:', error);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      await performHealthCheck();
      updateSystemMetrics();
      setLoading(false);
    };

    initialize();

    const healthInterval = setInterval(performHealthCheck, 30000);
    const metricsInterval = setInterval(updateSystemMetrics, 5000);

    return () => {
      clearInterval(healthInterval);
      clearInterval(metricsInterval);
    };
  }, []);

  const styles = {
    container: {
      padding: '0',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '0.5rem',
      letterSpacing: '2px',
      textShadow: '0 0 20px rgba(0, 255, 245, 0.5)',
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 500,
    },
    section: {
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      color: '#e0e0ff',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    chartsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    gaugesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
    },
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          color: '#a0a0c0',
          fontFamily: "'Rajdhani', sans-serif",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <Server size={64} style={{ color: '#8b5cf6', marginBottom: '1rem' }} />
        </motion.div>
        <div style={{ fontSize: '1.2rem', marginTop: '1rem' }}>
          Initializing system diagnostics...
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={styles.container}
    >
      <div style={styles.header}>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.title}
        >
          SYSTEM MONITOR
        </motion.h1>
        <p style={styles.subtitle}>
          Real-time system health and performance metrics
        </p>
      </div>

      <div style={{ ...styles.section, marginBottom: '2rem' }}>
        <StatusIndicator 
          status={healthData.status} 
          lastCheck={healthData.lastCheck}
        />
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Activity size={28} style={{ color: '#00fff5' }} />
          Health Monitor
        </h2>
        
        <div style={styles.chartsGrid}>
          <HeartMonitorChart
            data={metrics.responseTime}
            label="API Response Time"
            color="#00fff5"
            unit="ms"
          />
          
          <HeartMonitorChart
            data={metrics.userCount}
            label="Active Users"
            color="#8b5cf6"
            unit=" users"
            onViewUsers={handleViewUsers}
          />
        </div>
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <Server size={28} style={{ color: '#ffaa00' }} />
          System Resources
        </h2>
        
        <div style={styles.gaugesGrid}>
          <CircularGauge
            value={metrics.cpuUsage}
            max={100}
            label="CPU Usage"
            unit="%"
            color="#00fff5"
            icon={Cpu}
          />
          
          <CircularGauge
            value={metrics.memoryUsage}
            max={100}
            label="Memory Usage"
            unit="%"
            color="#8b5cf6"
            icon={Database}
          />
          
          <CircularGauge
            value={metrics.diskUsage}
            max={100}
            label="Disk Usage"
            unit="%"
            color="#ffaa00"
            icon={HardDrive}
          />
          
          <CircularGauge
            value={metrics.activeConnections}
            max={300}
            label="Active Connections"
            unit=""
            color="#00ff88"
            icon={Zap}
          />
        </div>
      </div>

      <UsersModal
        isOpen={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        users={usersList}
        loading={loadingUsers}
      />
    </motion.div>
  );
};

export default SystemPage;